import React from 'react';
import { useState, useEffect, useCallback, useRef } from 'react';
import L from "leaflet";
import LocationSearch from './components/LocationSearch';
import PageList from './components/PageList';

const pagesCache = {};

const fetchCategories = () => {
    return fetch("/api/categories")
        .then((response) => response.json());
}

const fetchResults = ({categoryId, subCategoryId, serviceId, locationId, municipalityId, countyId, query, longitude, latitude, distance, isRemote}) => {
    const hasLocalPosition = longitude && latitude;
    const categoryPart = categoryId ? "categoryId=" + categoryId : "";
    const subCategoryPart = subCategoryId ? "&subCategoryId=" + subCategoryId : "";
    const servicePart = serviceId ? "&serviceId=" + serviceId : "";
    const locationPart = !hasLocalPosition && locationId ? "&locationId=" + locationId : "";
    const municipalityPart = !hasLocalPosition && municipalityId ? "&municipalityId=" + municipalityId : "";
    const countyPart = !hasLocalPosition && countyId ? "&countyId=" + countyId : "";
    const queryPart = query ? "&query=" + query : "";
    const localPositionPart = hasLocalPosition ? `&latitude=${latitude}&longitude=${longitude}` : "";
    const distancePart = hasLocalPosition ? `&distance=${distance}` : "";
    const isRemotePart = isRemote ? `&isRemote=true` : "";

    const cacheString = categoryPart + subCategoryPart + servicePart + locationPart + municipalityPart + countyPart + queryPart + localPositionPart + distancePart + isRemotePart;

    if (pagesCache[cacheString]) {
        return pagesCache[cacheString];
    }

    return fetch("/api/search?" + categoryPart + subCategoryPart + servicePart + locationPart + municipalityPart + countyPart + queryPart + localPositionPart + distancePart + isRemotePart)
        .then((response) => {
            const result = response.json()
            pagesCache[cacheString] = result;
            return result;
        });
}

const Search = () => {
    const [categories, setCategories] = useState([]);
    const [results, setResults] = useState(null);
    const [levelFilter, setLevelFilter] = useState('');
    const searchInputRef = useRef(null);

    const [searchParams, setSearchParams] = useState({
        query: '',
        categoryId: null,
        subCategoryId: null,
        serviceId: null,
        countyId: null,
        municipalityId: null,
        locationId: null,
        latitude: null,
        longitude: null,
        isRemote: null,
        distance: 50,
    });

    // Item truncation
    const [itemsExpanded, setItemsExpanded] = useState(false);
    const ITEMS_LIMIT = 12;

    // Location state
    const [locationMode, setLocationMode] = useState(null);
    const [selectedLocation, setSelectedLocation] = useState(null);
    const [localPosition, setLocalPosition] = useState(null);
    const [radius, setRadius] = useState(50);

    const [pendingSearchMode, setPendingSearchMode] = useState(null);
    const autoSubmitLocationRef = useRef(false);

    useEffect(() => {
        fetchCategories().then(setCategories);
        fetchResults({}).then(setResults);

        const handleStepCardClick = (e) => {
            const card = e.target.closest('[data-search-mode]');
            if (!card) return;
            setPendingSearchMode(card.getAttribute('data-search-mode'));
        };

        document.querySelectorAll('[data-search-mode]').forEach(el => {
            el.addEventListener('click', handleStepCardClick);
        });

        return () => {
            document.querySelectorAll('[data-search-mode]').forEach(el => {
                el.removeEventListener('click', handleStepCardClick);
            });
        };
    }, []);

    useEffect(() => {
        if (!pendingSearchMode) return;
        setPendingSearchMode(null);
        if (pendingSearchMode === 'category') {
            searchInputRef.current?.focus();
        } else if (pendingSearchMode === 'location') {
            autoSubmitLocationRef.current = true;
            handleLocationModeClick('current');
        } else if (pendingSearchMode === 'remote') {
            handleLocationModeClick('remote');
        }
    }, [pendingSearchMode]);

    useEffect(() => {
        if (autoSubmitLocationRef.current && localPosition) {
            autoSubmitLocationRef.current = false;
            handleLocationOk();
        }
    }, [localPosition]);

    const updateSearch = (paramOverrides) => {
        const newParams = { ...searchParams, ...paramOverrides };
        setSearchParams(newParams);
        fetchResults(newParams).then(setResults);
    };

    const updateSearchWithLocation = useCallback((location) => {
        const locParams = {
            countyId: null,
            municipalityId: null,
            locationId: null,
            latitude: null,
            longitude: null,
            isRemote: null,
            distance: 50,
        };

        if (location) {
            switch (location.type) {
                case 'county': locParams.countyId = location.id; break;
                case 'municipality': locParams.municipalityId = location.id; break;
                case 'location': locParams.locationId = location.id; break;
                case 'coordinates':
                    locParams.latitude = location.latitude;
                    locParams.longitude = location.longitude;
                    locParams.distance = location.distance || radius;
                    break;
                case 'remote': locParams.isRemote = true; break;
            }
        }

        updateSearch(locParams);
    }, [searchParams, radius]);

    // Derived state
    const selectedCategory = categories.find(c => c.id === searchParams.categoryId);
    const selectedSubCategory = selectedCategory?.subCategories?.find(sc => sc.id === searchParams.subCategoryId);
    const selectedService = (() => {
        if (!searchParams.serviceId) return null;
        if (selectedSubCategory) {
            return selectedSubCategory.services?.find(s => s.id === searchParams.serviceId);
        }
        if (selectedCategory) {
            return selectedCategory.services?.find(s => s.id === searchParams.serviceId);
        }
        return null;
    })();

    const categoriesWithContent = categories.filter(c => c.subCategories.length > 0 || c.services.length > 0);
    const filterLc = levelFilter.toLowerCase();

    // Filtered items for current drill-down level
    const filteredCategories = [...categoriesWithContent]
        .filter(c => !filterLc || c.name.toLowerCase().includes(filterLc))
        .sort((a, b) => a.name.localeCompare(b.name));

    const currentSubCategories = selectedCategory && !selectedSubCategory && !selectedService
        ? (selectedCategory.subCategories || []).filter(sc => sc.services.length > 0) : [];
    const filteredSubCategories = [...currentSubCategories]
        .filter(sc => !filterLc || sc.name.toLowerCase().includes(filterLc))
        .sort((a, b) => a.name.localeCompare(b.name));

    const currentCategoryServices = selectedCategory && !selectedSubCategory && !selectedService
        ? (selectedCategory.services || []) : [];
    const filteredCategoryServices = [...currentCategoryServices]
        .filter(s => !filterLc || s.name.toLowerCase().includes(filterLc))
        .sort((a, b) => a.name.localeCompare(b.name));

    const currentServices = selectedSubCategory && !selectedService
        ? (selectedSubCategory.services || []) : [];
    const filteredServices = [...currentServices]
        .filter(s => !filterLc || s.name.toLowerCase().includes(filterLc))
        .sort((a, b) => a.name.localeCompare(b.name));

    // Category handlers
    const handleCategoryClick = (c) => {
        setLevelFilter('');
        updateSearch(c.id === searchParams.categoryId
            ? { categoryId: null, subCategoryId: null, serviceId: null }
            : { categoryId: c.id, subCategoryId: null, serviceId: null });
    };

    const handleSubCategoryClick = (sc) => {
        setLevelFilter('');
        updateSearch(sc.id === searchParams.subCategoryId
            ? { subCategoryId: null, serviceId: null }
            : { subCategoryId: sc.id, serviceId: null });
    };

    const handleServiceClick = (s) => {
        setLevelFilter('');
        updateSearch(s.id === searchParams.serviceId
            ? { serviceId: null }
            : { serviceId: s.id });
    };

    const handleCategoryClear = () => {
        setLevelFilter('');
        updateSearch({ categoryId: null, subCategoryId: null, serviceId: null });
    };

    // Location derived state
    const isChooseActive = selectedLocation && ['county', 'municipality', 'location'].includes(selectedLocation.type);
    const isCurrentActive = selectedLocation?.type === 'coordinates';
    const isRemoteActive = selectedLocation?.type === 'remote';

    // Location handlers
    const handleLocationModeClick = (mode) => {
        if (mode === 'remote') {
            if (isRemoteActive) {
                handleLocationClear();
            } else {
                const loc = { type: 'remote', name: 'Digitalt möte' };
                setSelectedLocation(loc);
                setLocationMode(null);
                updateSearchWithLocation(loc);
            }
            return;
        }
        if (mode === 'choose' && isChooseActive) {
            handleLocationClear();
            return;
        }
        if (mode === 'current' && isCurrentActive) {
            handleLocationClear();
            return;
        }
        setLocationMode(locationMode === mode ? null : mode);
        if (mode === 'current' && locationMode !== 'current' && "geolocation" in navigator) {
            navigator.geolocation.getCurrentPosition(
                (pos) => setLocalPosition({ latitude: pos.coords.latitude, longitude: pos.coords.longitude }),
                (err) => console.error('Error getting location:', err)
            );
        }
    };

    const handleLocationSearchSelect = (location) => {
        setSelectedLocation(location);
        setLocationMode(null);
        updateSearchWithLocation(location);
    };

    const handleLocationOk = () => {
        if (localPosition) {
            const loc = { type: 'coordinates', ...localPosition, name: 'Min position', distance: radius };
            setSelectedLocation(loc);
            setLocationMode(null);
            updateSearchWithLocation(loc);
        }
    };

    const handleLocationClear = () => {
        setSelectedLocation(null);
        setLocalPosition(null);
        setLocationMode(null);
        setRadius(50);
        updateSearchWithLocation(null);
    };

    const handleClearAll = () => {
        setLevelFilter('');
        setSelectedLocation(null);
        setLocalPosition(null);
        setLocationMode(null);
        setRadius(50);
        const resetParams = {
            categoryId: null, subCategoryId: null, serviceId: null,
            countyId: null, municipalityId: null, locationId: null,
            latitude: null, longitude: null, isRemote: null, distance: 50,
            query: '',
        };
        setSearchParams(resetParams);
        fetchResults(resetParams).then(setResults);
    };

    // Backspace removes the chip closest to the input (rightmost first)
    const handleSearchKeyDown = (e) => {
        if (e.key === 'Backspace' && !searchParams.query) {
            if (selectedLocation) handleLocationClear();
            else if (selectedService) handleServiceClick(selectedService);
            else if (selectedSubCategory) handleSubCategoryClick(selectedSubCategory);
            else if (selectedCategory) handleCategoryClick(selectedCategory);
        }
    };

    // Computed current level
    const currentLevel = selectedService ? 'done'
        : selectedSubCategory ? 'services'
        : selectedCategory ? 'subcategories'
        : 'categories';

    // Reset truncation when level changes or filter is active
    useEffect(() => setItemsExpanded(false), [currentLevel]);

    const truncate = (items) => !itemsExpanded && !levelFilter && items.length > ITEMS_LIMIT
        ? items.slice(0, ITEMS_LIMIT) : items;
    const overflowCount = (items) => !levelFilter && items.length > ITEMS_LIMIT
        ? items.length - ITEMS_LIMIT : 0;

    const hasAnySelection = selectedCategory || selectedLocation || searchParams.query;

    const filterPlaceholder = currentLevel === 'services' ? 'Filtrera tjänster...'
        : currentLevel === 'subcategories' ? 'Filtrera underkategorier & tjänster...'
        : 'Filtrera kategorier...';

    const levelClass = currentLevel === 'services' ? 'search-level--service'
        : currentLevel === 'subcategories' ? 'search-level--subcategory'
        : currentLevel === 'done' ? 'search-level--location'
        : 'search-level--category';

    // Location chip icon
    const locationIcon = selectedLocation?.type === 'remote' ? 'fa-solid fa-computer'
        : selectedLocation?.type === 'coordinates' ? 'fa-solid fa-location-crosshairs'
        : 'fa-solid fa-location-dot';

    return <>
        <div className="mb-3 service-container">
            {/* Search bar with inline selection chips */}
            <div className="search-query">
                <i className="fa-solid fa-magnifying-glass"></i>
                {selectedCategory && <button className="search-query__chip search-query__chip--category" onClick={() => handleCategoryClick(selectedCategory)}>
                    <i className="fa-solid fa-tag"></i> {selectedCategory.name} <i className="fa fa-xmark"></i>
                </button>}
                {selectedSubCategory && <button className="search-query__chip search-query__chip--subcategory" onClick={() => handleSubCategoryClick(selectedSubCategory)}>
                    <i className="fa-solid fa-tags"></i> {selectedSubCategory.name} <i className="fa fa-xmark"></i>
                </button>}
                {selectedService && <button className="search-query__chip search-query__chip--service" onClick={() => handleServiceClick(selectedService)}>
                    <i className="fa-solid fa-hand-holding-heart"></i> {selectedService.name} <i className="fa fa-xmark"></i>
                </button>}
                {selectedLocation && <button className="search-query__chip search-query__chip--location" onClick={handleLocationClear}>
                    <i className={locationIcon}></i> {selectedLocation.name}
                    {selectedLocation.type === 'coordinates' && ` (${radius} km)`}
                    <i className="fa fa-xmark"></i>
                </button>}
                <input
                    ref={searchInputRef}
                    type="text"
                    value={searchParams.query}
                    onChange={(e) => updateSearch({ query: e.target.value })}
                    onKeyDown={handleSearchKeyDown}
                    placeholder="Sök logoped, tjänst, ort..."
                />
                <button
                    className={`search-query__clear ${hasAnySelection ? '' : 'search-query__clear--hidden'}`}
                    onClick={handleClearAll}
                >
                    <i className="fa fa-xmark"></i>
                </button>
            </div>

            {/* Unified filter panel */}
            <div className={`search-level ${levelClass}`}>
                {/* Category drill-down */}
                {currentLevel !== 'done' && <>
                    <div className="search-level__header">
                        <div className="search-level__filter">
                            <i className="fa-solid fa-magnifying-glass"></i>
                            <input
                                type="text"
                                value={levelFilter}
                                onChange={(e) => setLevelFilter(e.target.value)}
                                placeholder={filterPlaceholder}
                            />
                        </div>
                        {currentLevel !== 'categories' && <button className="btn btn-sm btn-clear" onClick={handleCategoryClear}>
                            Rensa <i className="fa fa-circle-xmark"></i>
                        </button>}
                    </div>

                    {/* Categories */}
                    {currentLevel === 'categories' && <div className="search-level__items">
                        {truncate(filteredCategories).map(c =>
                            <button key={c.id} onClick={() => handleCategoryClick(c)} className="btn btn-category pill">
                                <i className="fa-solid fa-tag"></i> {c.name}
                            </button>
                        )}
                        {!itemsExpanded && overflowCount(filteredCategories) > 0 &&
                            <button className="search-level__more" onClick={() => setItemsExpanded(true)}>
                                +{overflowCount(filteredCategories)} fler <i className="fa-solid fa-chevron-down"></i>
                            </button>}
                        {itemsExpanded && filteredCategories.length > ITEMS_LIMIT &&
                            <button className="search-level__less" onClick={() => setItemsExpanded(false)}>
                                Visa färre <i className="fa-solid fa-chevron-up"></i>
                            </button>}
                        {filteredCategories.length === 0 && <p className="search-level__empty">Inga kategorier matchar <strong>"{levelFilter}"</strong></p>}
                    </div>}

                    {/* Subcategories + category services (colors distinguish them) */}
                    {currentLevel === 'subcategories' && (() => {
                        const combined = [
                            ...filteredSubCategories.map(sc => ({ ...sc, _kind: 'sub' })),
                            ...filteredCategoryServices.map(s => ({ ...s, _kind: 'svc' })),
                        ];
                        const visible = truncate(combined);
                        return <div className="search-level__items">
                            {visible.map(item => item._kind === 'sub'
                                ? <button key={item.id} onClick={() => handleSubCategoryClick(item)} className="btn btn-sub-category pill">
                                    <i className="fa-solid fa-tags"></i> {item.name}
                                </button>
                                : <button key={item.id} onClick={() => handleServiceClick(item)} className="btn btn-service pill">
                                    <i className="fa-solid fa-hand-holding-heart"></i> {item.name}
                                </button>
                            )}
                            {!itemsExpanded && overflowCount(combined) > 0 &&
                                <button className="search-level__more" onClick={() => setItemsExpanded(true)}>
                                    +{overflowCount(combined)} fler <i className="fa-solid fa-chevron-down"></i>
                                </button>}
                            {itemsExpanded && combined.length > ITEMS_LIMIT &&
                                <button className="search-level__less" onClick={() => setItemsExpanded(false)}>
                                    Visa färre <i className="fa-solid fa-chevron-up"></i>
                                </button>}
                            {combined.length === 0 &&
                                <p className="search-level__empty">Inga resultat matchar <strong>"{levelFilter}"</strong></p>}
                        </div>;
                    })()}

                    {/* Services */}
                    {currentLevel === 'services' && <div className="search-level__items">
                        {truncate(filteredServices).map(s =>
                            <button key={s.id} onClick={() => handleServiceClick(s)} className="btn btn-service pill">
                                <i className="fa-solid fa-hand-holding-heart"></i> {s.name}
                            </button>
                        )}
                        {!itemsExpanded && overflowCount(filteredServices) > 0 &&
                            <button className="search-level__more" onClick={() => setItemsExpanded(true)}>
                                +{overflowCount(filteredServices)} fler <i className="fa-solid fa-chevron-down"></i>
                            </button>}
                        {itemsExpanded && filteredServices.length > ITEMS_LIMIT &&
                            <button className="search-level__less" onClick={() => setItemsExpanded(false)}>
                                Visa färre <i className="fa-solid fa-chevron-up"></i>
                            </button>}
                        {filteredServices.length === 0 && <p className="search-level__empty">Inga tjänster matchar <strong>"{levelFilter}"</strong></p>}
                    </div>}
                </>}

                {/* Location section - always visible */}
                <div className="search-level__location">
                    <div className="location-pills">
                        <button
                            className={`location-pill ${locationMode === 'choose' || isChooseActive ? 'location-pill--active' : ''}`}
                            onClick={() => handleLocationModeClick('choose')}
                        >
                            <i className="fa fa-earth-europe"></i> Välj plats
                        </button>
                        <button
                            className={`location-pill ${locationMode === 'current' || isCurrentActive ? 'location-pill--active' : ''}`}
                            onClick={() => handleLocationModeClick('current')}
                        >
                            <i className="fa-solid fa-location-crosshairs"></i> Din position
                        </button>
                        <button
                            className={`location-pill ${isRemoteActive ? 'location-pill--active' : ''}`}
                            onClick={() => handleLocationModeClick('remote')}
                        >
                            <i className="fa-solid fa-computer"></i> Digitalt möte
                        </button>
                    </div>
                    {locationMode === 'choose' && <div className="location-expand">
                        <LocationSearch
                            onLocationChange={handleLocationSearchSelect}
                            unfocus={() => setLocationMode(null)}
                        />
                    </div>}
                    {locationMode === 'current' && <div className="location-expand location-expand--radius">
                        <div className="location-expand__slider">
                            <div className="location-expand__radius-value">
                                {radius} <span>km</span>
                            </div>
                            <input
                                type="range"
                                className="form-range"
                                min="1"
                                max="300"
                                value={radius}
                                onChange={(e) => setRadius(parseInt(e.target.value))}
                            />
                            <div className="location-expand__radius-labels">
                                <span>1 km</span>
                                <span>300 km</span>
                            </div>
                        </div>
                        <button className="btn btn-primary location-expand__submit" onClick={handleLocationOk} disabled={!localPosition}>
                            <i className="fa-solid fa-location-crosshairs"></i> {localPosition ? 'Sök' : 'Väntar...'}
                        </button>
                    </div>}
                </div>
            </div>
        </div>
        <PageList pageIds={results} params={searchParams} />
    </>;
}

export default Search;
